/**
 * A non-2xx answer, with its status kept as a number.
 *
 * The message is exactly what `apiFetch` always threw — `"<status>
 * <statusText>: <detail>"` — and `name` stays `Error`, so every page that
 * renders `err.message` or `String(err)` reads the same as before. The
 * status is for a page that has to tell answers apart: the Agenda says
 * something different for 404 (a core too old to serve the route), 403 (a
 * role below the route's) and anything else, and reading the number out
 * of a message is how such a distinction quietly stops working.
 */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export async function apiFetch<T>(
  path: string,
  opts: RequestInit = {},
): Promise<T> {
  const url = path.startsWith('/') ? path : `/api/${path}`;
  const response = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers ?? {}) },
    ...opts,
  });

  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = body?.error ?? JSON.stringify(body);
    } catch {
      detail = await response.text();
    }
    throw new ApiError(
      response.status,
      `${response.status} ${response.statusText}: ${detail || path}`,
    );
  }

  return (await response.json()) as T;
}
